import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiX } from 'react-icons/fi';
import { gsidaPublicationsApi, fileUploadApi } from '../../api/phpApi';
import './AdminManage.css';

export default function AdminPublications() {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    publication_date: '',
    type: '',
    short_description: '',
    full_description: '',
    pdf_url: '',
    cover_image: ''
  });

  // Fetch publications
  useEffect(() => {
    loadPublications();
  }, []);

  const loadPublications = async () => {
    try {
      setLoading(true);
      const data = await gsidaPublicationsApi.getAll();
      setPublications(data || []);
    } catch (err) {
      setError('Failed to load publications: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file uploads
  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      let url;
      if (type === 'cover') {
        url = await fileUploadApi.uploadImage(file, 'images', 'publications');
        setFormData(prev => ({ ...prev, cover_image: url }));
        setCoverPreview(url);
      } else if (type === 'pdf') {
        url = await fileUploadApi.uploadPdf(file, 'publications');
        setFormData(prev => ({ ...prev, pdf_url: url }));
      }
    } catch (err) {
      setError('Failed to upload file: ' + err.message);
    }
  };

  // Handle add/edit submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await gsidaPublicationsApi.update(editingId, formData);
        setError('');
      } else {
        await gsidaPublicationsApi.create(formData);
        setError('');
      }
      resetForm();
      loadPublications();
    } catch (err) {
      setError('Failed to save publication: ' + err.message);
    }
  };

  // Handle edit
  const handleEdit = (pub) => {
    setFormData(pub);
    setCoverPreview(pub.cover_image);
    setEditingId(pub.id);
    setIsFormOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this publication?')) {
      try {
        await gsidaPublicationsApi.delete(id);
        loadPublications();
      } catch (err) {
        setError('Failed to delete publication: ' + err.message);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      publication_date: '',
      type: '',
      short_description: '',
      full_description: '',
      pdf_url: '',
      cover_image: ''
    });
    setCoverPreview('');
    setEditingId(null);
    setIsFormOpen(false);
  };

  if (loading) return <div className="admin-loading">Loading publications...</div>;

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Manage GSIDA Publications</h2>
        <button 
          className="btn-primary btn-add"
          onClick={() => setIsFormOpen(true)}
        >
          <FiPlus /> Add Publication
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingId ? 'Edit Publication' : 'Add New Publication'}</h3>
              <button className="btn-close" onClick={resetForm}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group form-row full">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Publication Date *</label>
                  <input
                    type="date"
                    name="publication_date"
                    value={formData.publication_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select type</option>
                    <option value="Rapport Stratégique">Rapport Stratégique</option>
                    <option value="Livre Blanc">Livre Blanc</option>
                    <option value="Étude Technique">Étude Technique</option>
                    <option value="Rapport de Synthèse">Rapport de Synthèse</option>
                  </select>
                </div>
              </div>

              <div className="form-group form-row full">
                <label>Short Description *</label>
                <textarea
                  name="short_description"
                  value={formData.short_description}
                  onChange={handleInputChange}
                  rows="2"
                  required
                />
              </div>

              <div className="form-group form-row full">
                <label>Full Description</label>
                <textarea
                  name="full_description"
                  value={formData.full_description}
                  onChange={handleInputChange}
                  rows="4"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cover Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'cover')}
                  />
                  {coverPreview && (
                    <img src={coverPreview} alt="Cover Preview" className="image-preview" />
                  )}
                </div>
                <div className="form-group">
                  <label>PDF Document</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, 'pdf')}
                  />
                  {formData.pdf_url && (
                    <p style={{ fontSize: '0.85rem', color: '#16a34a', marginTop: '8px' }}>
                      ✓ PDF uploaded
                    </p>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <FiSave /> Save Publication
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Publications Table */}
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Publication Date</th>
              <th>PDF</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {publications.length === 0 ? (
              <tr><td colSpan="5" className="text-center">No publications found</td></tr>
            ) : (
              publications.map(pub => (
                <tr key={pub.id}>
                  <td>{pub.title}</td>
                  <td><span style={{ backgroundColor: '#e0e7ff', padding: '4px 12px', borderRadius: '4px', fontSize: '0.85rem' }}>{pub.type}</span></td>
                  <td>{pub.publication_date}</td>
                  <td>{pub.pdf_url ? '✓ Uploaded' : 'N/A'}</td>
                  <td className="actions">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(pub)}
                      title="Edit"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(pub.id)}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
